<template>
  <main v-if="quoteRequest">
    <!---->
    <button
      class="btn-fixed btn-toggle-messages"
      v-bind:class="{'btn-fixed--active': showMessages}"
      @click="toggleMessages"
    >
      <i class="far fa-comments"></i>
      <span>Messages</span>
      <span
        class="tag is-danger badge"
        v-if="newMessages.length > 0 && !showMessages"
      >{{newMessages.length}}</span>
    </button>
    <!---->
    <button
      class="btn-fixed btn-toggle-status"
      _v-if="acceptStatus !== 'Opened'"
      v-bind:class="{'btn-fixed--active': statusMenu}"
      @click="toggleStatusMenu"
    >
      <i class="far" v-bind:class="computedQuoteStatusClass"></i>
      <span>{{acceptStatus}}</span>
    </button>
    <progress-bar v-if="acceptStatusValid && !showQuoteInBX" :step="step" :step-reached="stepReached"></progress-bar>
    <div class="columns is-gapless">
      <div id="main-column" class="column">
        <header-bar
          :date-deadline="formattedDateDeadline"
          :quote-request="quoteRequest"
          :date-submitted="formattedDateSubmitted"
          :step="step"
          :step-reached="stepReached"
        ></header-bar>
        <message-error
          v-if="connectionError"
          message-content="Oops, the connection to the server has dropped out."
          reload-button="true"
        ></message-error>
        <message-error
          v-if="saveError"
          :message-content="saveErrorMessage"
        ></message-error>
        <form action id="quoteRequestForm" method="post" @submit.prevent="validateBeforeSubmit">
          <!-- Container for owl nav must be outside of padded wrappers in sections -->
          <div class="owl-arrows is-hidden-tablet" v-if="step === 2"></div>
          <section class="section">
            <!-- -->
            <section-details
              v-if="step === 1"
              :date-deadline="formattedDateDeadline"
              :quote-request="quoteRequest"
              :date-submitted="formattedDateSubmitted"
              :token="id"
              :step="step"
              :step-reached="stepReached"
              v-on:toggleMessagePane="showMessagePane"
            ></section-details>
            <!-- -->
            <section-scope-basic
              v-if="step === 1"
              :quote-request="quoteRequest"
              :date-deadline="formattedDateDeadline"
              :date-submitted="formattedDateSubmitted"
              :token="id"
              :step="step"
              :step-reached="stepReached"
              v-on:toggleMessagePane="showMessagePane"
            ></section-scope-basic>
            <!-- -->
            <section-accept
              :date-deadline="formattedDateDeadline"
              :quote-request="quoteRequest"
              :date-submitted="formattedDateSubmitted"
              :token="id"
              :step="step"
              :step-reached="stepReached"
              :status-menu="statusMenu"
              :reopen-visible="reopenVisible"
              v-if="!showQuoteInBX"
            ></section-accept>
            <section-quote-in-bx v-if="step === 2 && showQuoteInBX"></section-quote-in-bx>
            <!-- -->
            <section-items
              v-if="step === 2 && !showQuoteInBX"
              :quote-request="quoteRequest"
              :date-deadline="formattedDateDeadline"
              :date-submitted="formattedDateSubmitted"
              :step="step"
              :step-reached="stepReached"
              :reopen-visible="reopenVisible"
              v-on:toggleMessagePane="showMessagePane"
            ></section-items>
            <!---->
            <section-form
              v-if="step === 3"
              :date-deadline="formattedDateDeadline"
              :quote-request="quoteRequest"
              :date-submitted="formattedDateSubmitted"
              :step="step"
              :step-reached="stepReached"
              :token="id"
              v-on:updateRecipientResponseText="updateRecipientResponseText"
              v-on:updateRecipientEmailCC="updateRecipientEmailCC"
              v-on:toggleMessagePane="showMessagePane"
            ></section-form>
            <!---->
            <section-submitted
              v-if="step === 4"
              :date-deadline="formattedDateDeadline"
              :quote-request="quoteRequest"
              :date-submitted="formattedDateSubmitted"
              :step="step"
              :step-reached="stepReached"
            ></section-submitted>
          </section>
          <footer-nav
            :submitting="submitting"
            :quote-request="quoteRequest"
            :submitted-success="submittedSuccess"
            :step="step"
            :step-reached="stepReached"
            :token="id"
            :item-sub-total="computedItemSubTotal"
            :item-tax-total="computedItemTaxTotal"
            :item-total="computedItemTotal"
            :formatted-date-submitted="formattedDateSubmitted"
            :date-deadline="formattedDateDeadline"
            v-on:stepChanged="stepChanged"
            v-on:updateTaxCheckbox="updateTaxCheckbox"
            v-if="!showQuoteInBX"
          ></footer-nav>
        </form>
      </div>
    </div>
    <aside-discussion
      _v-if="step < 3"
      v-show="showMessages"
      class="column"
      :quote-request="quoteRequest"
      :messages="messages"
      :show-messages="showMessages"
      :step="step"
      :step-reached="stepReached"
      v-on:closeNotification="toggleMessages"
    ></aside-discussion>
  </main>
</template>

<script>
import $ from 'jquery'

// 3rd Party
import axios from 'axios'
import moment from 'moment'
import Mailcheck from 'mailcheck'

// Components
import HeaderBar from '@/components/Header.vue'
import FooterNav from '@/components/Footer.vue'

// Section components
import SectionForm from '@/components/pages/sections/SectionForm.vue'
import SectionItems from '@/components/pages/sections/SectionItems.vue'
import AsideDiscussion from '@/components/pages/sections/AsideDiscussion.vue'
import SectionAccept from '@/components/pages/sections/SectionAccept.vue'
import SectionDetails from '@/components/pages/sections/SectionDetails.vue'
import SectionSubmitted from '@/components/pages/sections/SectionSubmitted.vue'
import MessageError from '@/components/alerts/MessageError.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import SectionScopeBasic from '@/components/pages/sections/SectionScopeBasic.vue'
import SectionQuoteInBx from '@/components/pages/sections/SectionQuoteInBx.vue'
import * as signalR from '@microsoft/signalr'

export default {
  name: 'Home-Screen',
  inject: ['$validator'],
  created () {
    let scope = this
    this.$store.commit('storeToken', this.$route.params.id)
    // get Data
    const AuthStr = `Bearer ${this.$route.params.id}`
    axios
      .get(`${window.APP_CONFIG.apiServiceUrl}quoterequest/portal/request`, {
        headers: {Authorization: AuthStr}
      })
      .then(response => {
        this.$store.commit('storeData', response.data)
        // Set locale based on company
        this.$i18n.locale = response.data.company.locale
        // If already submitted, always start at the 1st step, instead of the stored step
        this.$store.commit('updateStep', response.data.recipient.dateSubmitted ? 1 : (response.data.recipient.step || 1))
        this.$store.commit('updateStepReached', response.data.recipient.stepReached || null)
        this.$store.commit('updateReopenVisible', response.data.recipient.dateSubmitted !== null)
        scope.MailSuggestion = Mailcheck.splitEmail(response.data.recipient.email)
        scope.UseDomainAsCompany = [...this.MailCheckDefaultDomains, ...this.CheckAUDomains].indexOf(scope.MailSuggestion.domain) === -1

        // const href = window.location.href
        // if (href.indexOf('localhost') === -1) {
        // set pendo parameters
        let accountId = scope.UseDomainAsCompany ? scope.MailSuggestion.domain : response.data.recipient.email
        let accountName = response.data.company.name || accountId
        //
        let visitorId = response.data.recipient.email
        // Send to pendo (global window declared in head script)
        if (!window.pendo) return
        //
        let pendoData = {
          visitor: {id: visitorId},
          account: {id: accountId, name: accountName}
        }
        window.pendo.initialize(pendoData)
        // }
      })
      .catch(error => {
        console.log('error', error)
      })

    this.connectToSignalR()
    this.getMessages()
  },
  unmounted () {
    this.disconnectSignalR()
  },
  data () {
    return {
      signalRConnection: signalR.HubConnection,
      showMessages: false,
      savedSuccess: null,
      saving: false,
      submitting: false,
      saveTimeout: null,
      messages: [],
      newMessages: [],
      newMessageBody: '',
      connectionError: false,
      saveError: false,
      saveErrorMessage: '',
      statusMenu: false,
      MailSuggestion: null,
      MailCheckDefaultDomains: Mailcheck.defaultDomains,
      CheckAUDomains: [
        'hotmail.com.au',
        'live.com.au',
        'yahoo.com.au',
        'y7mail.com',
        'optusnet.com.au',
        'bigpond.net.au',
        'hotmail.com.au',
        'aapt.net.au',
        'bigpond.com.au',
        'iinet.net.au',
        'outlook.com.au',
        'ozemail.com.au',
        'hotmail.co.nz',
        'live.co.nz',
        'yahoo.co.nz'
      ],
      UseDomainAsCompany: null
    }
  },
  methods: {
    connectToSignalR () {
      // Build a signalR connection
      const signalRConnectionUrl = `${window.APP_CONFIG.apiServiceUrl}notifications/rfqSignalr`

      this.signalRConnection = new signalR.HubConnectionBuilder()
        .withUrl(signalRConnectionUrl, {accessTokenFactory: () => this.$route.params.id})
        .withAutomaticReconnect([2000, 3000, 5000, 7000, 10000, 15000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build()

      // Start the signalR connection
      this.signalRConnection.start()
        .then(() => {
          this.connectionError = false
          // eslint-disable-next-line no-return-assign
          this.signalRConnection.onclose((_error) => this.connectionError = true)
          // eslint-disable-next-line no-return-assign
          this.signalRConnection.onreconnected((connectionId) => this.connectionError = false)
        })
        .catch((reason) => {
          console.error('SignalR connection failed')
          this.connectionError = true
        })

      // Listen to notifyMessageSent event
      this.signalRConnection.on('notifyMessageSent', this.newMessageReceived)
    },
    disconnectSignalR () {
      if (this.signalRConnection) {
        this.signalRConnection.off('notifyMessageSent', this.newMessageReceived)
        if (this.signalRConnection.state === signalR.HubConnectionState.Connected) {
          this.signalRConnection.stop()
        }
      }
    },
    newMessageReceived (message) {
      this.messages.push({
        isNew: true,
        postDate: message.PostDate,
        recipientId: message.ID_Recipient,
        senderId: message.ID_Sender,
        text: message.Body,
        type: message.Type,
        time: moment(message.PostDate).format('MMM D YYYY, h:mm a')
      })
      if (message.ID_Sender !== this.ID_Recipient) {
        this.newMessages.push({
          body: message.Body,
          type: message.Type
        })
      }
    },
    getMessages () {
      const AuthStr = `Bearer ${this.id}`
      axios
        .get(`${window.APP_CONFIG.apiServiceUrl}quoterequest/portal/messages`, {
          headers: {Authorization: AuthStr}
        })
        .then(response => {
          if (response.status === 200) {
            //
            this.messages = []
            //
            response.data.forEach((message, index, array) => {
              if (message.type === 'Info') {
                // Only do this for messages (type: Info)
                message.time = moment(message.postDate).format(
                  'MMM D YYYY, h:mm a'
                )
                this.messages.push(message)
                if (message.isNew) this.newMessages.push(message)
              }
            })
          }
        })
        .catch(error => {
          console.log('Message send error', error, this)
        })
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then(result => {
        if (result) {
          //
          // POST TO SUBMIT API HERE WITH THA DATA
          // return confirm('Confirm Submit Quote?') ? this.quoteRequestSubmit() : null
          this.quoteRequestSubmit()
        } else {
          console.error('Not valid quote - input required')
        }
      })
    },
    updateRecipientResponseText (e) {
      this.$store.commit('updateRecipientResponseText', e.target.value)
      this.onSaveTimeout()
    },
    updateRecipientEmailCC (e) {
      this.$store.commit('updateRecipientEmailCC', e.target.value)
      this.onSaveTimeout()
    },
    updateTaxCheckbox (checked) {
      this.$store.commit('updateRecipientTaxRegistration', checked)
      this.onSaveTimeout()
      // Will update totals, and subsequently save will be called.
    },
    stepChanged () {
      this.onSaveTimeout()
    },
    showMessagePane () {
      this.newMessages = []
      this.showMessages = true
      //
      setTimeout(() => {
        $('html, body').animate(
          {scrollTop: $('#toggle_messages').offset().top - 150},
          250
        )
        $('.message-field').focus()
      }, 50)
    },
    toggleMessages () {
      this.newMessages = []
      this.showMessages = !this.showMessages
      //
      setTimeout(() => {
        $('html, body').animate(
          {scrollTop: $('#toggle_messages').offset().top - 150},
          250
        )
        $('.message-field').focus()
      }, 50)
    },
    // Endpoints
    quoteRequestSave () {
      // Don't save when it's already been submitted, this prevents a 400 from the server
      if (this.quoteRequest.recipient.dateSubmitted) return

      const AuthStr = `Bearer ${this.id}`
      this.saving = true
      axios
        .post(
          `${window.APP_CONFIG.apiServiceUrl}quoterequest/portal/save`,
          this.quoteRequest,
          {
            headers: {Authorization: AuthStr}
          }
        )
        .then(response => {
          if (response.status === 200) {
            this.savedSuccess = true
            this.saveError = false
            this.saving = false
            setTimeout(() => {
              this.savedSuccess = null
            }, 3 * 1000)
          }
        })
        .catch(error => {
          console.log('Save error', error, this)
          this.saving = false
          this.savedSuccess = false
          this.setSaveError(error, 'Sorry, but an error occurred trying to save the quote.')
        })
    },
    quoteRequestSubmit () {
      const AuthStr = `Bearer ${this.id}`
      this.submitting = true
      this.submittedSuccess = false
      axios
        .post(
          `${window.APP_CONFIG.apiServiceUrl}quoterequest/portal/submit`,
          this.quoteRequest,
          {
            headers: {Authorization: AuthStr}
          }
        )
        .then(response => {
          if (response.status === 200) {
            this.submittedSuccess = true
            this.saveError = false
            this.instructions = false
            this.submitting = false
            window.scrollTo(0, 0)
            this.$store.commit('updateDateSubmitted', moment().format())
            this.$store.commit('updateStep', 4)
            this.$store.commit('updateStepReached', 4)
            this.$store.commit('setQuoteStatus', 'Submitted')
            this.$store.commit('updateReopenVisible', true)
          }
        })
        .catch(error => {
          console.error(error)
          this.submitting = false
          this.submittedSuccess = false
          this.setSaveError(error, 'Sorry, but an error occurred trying to submit the quote.')
        })
    },
    onSaveTimeout () {
      clearTimeout(this.saveTimeout)
      this.saveTimeout = setTimeout(this.quoteRequestSave, 1000)
    },
    closeNotification (messageIndex) {
      let message = this.newMessages[messageIndex]
      const AuthStr = `Bearer ${this.id}`
      axios
        .post(
          `${window.APP_CONFIG.apiServiceUrl}quoterequest/portal/messages/${
            message.id
          }/read`,
          this.quoteRequest,
          {
            headers: {Authorization: AuthStr}
          }
        )
        .then(response => {
          if (response.status === 200) {
            this.newMessages.splice(messageIndex, 1) // [] // = false
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    toggleStatusMenu () {
      this.statusMenu = !this.statusMenu
    },
    setSaveError (error, msg) {
      this.saveError = true
      if (error.response && error.response.data && error.response.data.message) {
        this.saveErrorMessage = 'Save failed: ' + error.response.data.message
      } else {
        this.saveErrorMessage = msg
      }
    }
  },
  components: {
    SectionScopeBasic,
    ProgressBar,
    MessageError,
    SectionSubmitted,
    SectionAccept,
    SectionDetails,
    AsideDiscussion,
    SectionItems,
    SectionForm,
    HeaderBar,
    FooterNav,
    SectionQuoteInBx
  },
  watch: {},
  computed: {
    id () {
      return this.$store.state.token
    },
    ID_Recipient () {
      return this.$store.state.data.recipient.iD_QuoteRequestRecipient
    },
    quoteRequest () {
      return this.$store.state.data
    },
    formattedDateDeadline () {
      return moment(this.quoteRequest.dateDeadline).format('ddd MMM Do Y')
    },
    formattedDateSubmitted () {
      if (
        this.quoteRequest &&
        this.quoteRequest.recipient &&
        this.quoteRequest.recipient !== null &&
        this.quoteRequest.recipient.dateSubmitted !== null
      ) {
        return moment(this.quoteRequest.recipient.dateSubmitted).format(
          'ddd MMM Do Y'
        )
      } else {
        return ''
      }
    },
    computedItemSubTotal: {
      get: function () {
        let total = 0.0
        for (let x in this.$store.state.data.items) {
          if ((this.computedRegisteredForTax || (this.quoteRequest.calculationMethod === 1)) && (this.$store.state.data.items[x].response.taxStatus === 0)) {
            total += this.$store.state.data.items[x].response.total - this.$store.state.data.items[x].response.tax
          } else {
            total += this.$store.state.data.items[x].response.total
          }
        }
        total = Math.round(total * 100) / 100 // Round to 2 decimal places.
        let computedValue = this.$n(total, 'currency')
        return computedValue
      },
      set: function (newVal, oldVal) {
        // ?
      }
    },
    computedItemTaxTotal: {
      get: function () {
        let total = 0.0
        if (this.computedRegisteredForTax || (this.quoteRequest.calculationMethod === 1)) {
          for (let x in this.$store.state.data.items) {
            total += this.$store.state.data.items[x].response.tax
          }
        }
        total = Math.round(total * 100) / 100 // Round to 2 decimal places.
        let computedValue = this.$n(total, 'currency')
        return computedValue
      },
      set: function (newVal, oldVal) {
        // ?
      }
    },
    computedItemTotal: {
      get: function () {
        if (this.$store.state.data.recipient) {
          let total = 0.0
          for (let x in this.$store.state.data.items) {
            total += this.$store.state.data.items[x].response.totalIncTax
          }
          total = Math.round(total * 100) / 100 // Round to 2 decimal places.
          this.$store.commit('updateRecipientTotalAmount', total)
          this.onSaveTimeout()
          return this.$n(total, 'currency')
        } else {
          return 0
        }
      },
      set: function (newVal, oldVal) {
        // ?
      }
    },
    computedRegisteredForTax: {
      get: function () {
        //
        if (this.$store.state.data && this.$store.state.data.recipient) {
          return this.$store.state.data.recipient.isRegisteredForTax
        } else {
          return true // WEB-1014 - changed from null to true here, to attempt to make default as true
        }
      },
      set: function (oldVal, newVal) {
        //
      }
    },
    submittedSuccess: {
      get: function () {
        //
        if (this.$store.state) {
          return this.$store.state.submittedSuccess
        } else { return false }
      },
      set: function (oldVal, newVal) {
        //
        this.$store.commit('submittedSuccess', newVal)
      }
    },
    showQuoteInBX: {
      get: function () {
        if (this.$store.state) {
          return this.$store.state.showQuoteInBX
        } else { return false }
      },
      set: function (newVal) {
        this.$store.commit('updateShowQuoteInBX', newVal)
      }
    },
    step () {
      if (
        this.$store.state.data &&
        this.$store.state.data.recipient &&
        !isNaN(this.$store.state.data.recipient.step)
      ) {
        return this.$store.state.data.recipient.step
      } else {
        return 0
      }
    },
    stepReached () {
      if (this.$store.state.data && this.$store.state.data.recipient) {
        return this.$store.state.data.recipient.stepReached
      } else {
        return 1
      }
    },
    acceptStatusValid () {
      // Don't include Opened
      // this.$store.state.data.recipient.quoteStatus === 'Opened'
      return (
        (this.$store.state.data &&
          this.$store.state.data.recipient &&
          (this.$store.state.data.recipient.quoteStatus === 'Quoting' ||
            this.$store.state.data.recipient.quoteStatus === 'Submitted')) ||
        this.stepReached > 1
      )
    },
    acceptStatus () {
      // Don't include Opened
      // this.$store.state.data.recipient.quoteStatus === 'Opened'
      if (this.$store.state.data && this.$store.state.data.recipient) {
        return this.$store.state.data.recipient.quoteStatus === 'Opened'
          ? 'Pending'
          : this.$store.state.data.recipient.quoteStatus
      } else {
        return ''
      }
    },
    computedQuoteStatusClass () {
      switch (this.acceptStatus.toLowerCase()) {
        case 'quoting':
          return 'fa-comment-alt-edit'
        case 'opened':
        case 'pending':
        case 'notified':
          return 'fa-comment-alt-minus'
        case 'declined':
          return 'fa-comment-alt-times'
        case 'submitted':
          return 'fa-comment-alt-check'
        case 'accepted':
          return 'fa-handshake'
        default:
          return 'fa-comment-alt-minus'
      }
    },
    reopenVisible () {
      return this.$store.state.reopenVisible
    }
  }
}
</script>
